import { useEffect, useState } from 'react';
import { Box, Chip, IconButton } from '@mui/material';
import ArrowLeft from '@mui/icons-material/ArrowLeft';
import ArrowRight from '@mui/icons-material/ArrowRight';
import Text from '../../Atoms/Text';
import { chipStyles, getFormattedDate } from '../../../utils/helpers';
import { Run, fetchRuns, getRunsSubject, RunStatus } from '../../../store/Run';

interface RunSelectionProps {
  selectedRunId: number | null;
  setSelectedRun: (run: Run) => void;
  monthYear?: string;
  types?: RunStatus[];
  withLabel?: boolean;
}

export const RunSelection = ({
  selectedRunId,
  setSelectedRun,
  types,
  monthYear,
  withLabel = true
}: RunSelectionProps) => {
  const [eligibleRuns, setEligibleRuns] = useState<Run[]>([]);

  useEffect(() => {
    const subscription = getRunsSubject().subscribe((runs) => {
      const filteredRuns = types ? runs.filter((run) => types.includes(run.status)) : runs;
      setEligibleRuns(filteredRuns);
    });
    if (getRunsSubject().value.length === 0) fetchRuns();

    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (eligibleRuns.length > 0 && !selectedRunId) {
      const activeRun = eligibleRuns.find((r) => {
        return monthYear
          ? monthYear === getFormattedDate(r.date, 'MMM YY')
          : r.isActive && r.status === RunStatus.PROCESSING;
      });
      if (activeRun) {
        setSelectedRun(activeRun);
        return;
      }
      const processingRun = eligibleRuns.find((r) => {
        return r.status === RunStatus.PROCESSING;
      });
      if (processingRun) setSelectedRun(processingRun);
    }
  }, [eligibleRuns, selectedRunId, monthYear]);

  const selectedRun = selectedRunId ? eligibleRuns.find((run) => run.id === selectedRunId) || null : null;
  const currentIndex = selectedRun ? eligibleRuns.findIndex((run) => run.id === selectedRun.id) : -1;

  const getRunDate = (offset: number | null = null) => {
    if (!selectedRun) return '';
    if (offset === null) return getFormattedDate(selectedRun.date, 'MMM YY');
    const date = eligibleRuns[currentIndex + offset]?.date;
    if (date) return getFormattedDate(date, 'MMM YY');
    const newDate = new Date(selectedRun.date);
    newDate.setMonth(newDate.getMonth() - offset);
    return getFormattedDate(newDate.toISOString(), 'MMM YY');
  };

  const setCurrentIndex = (index: number) => {
    const run = eligibleRuns[index];
    if (run) setSelectedRun(run);
  };

  const borderRadius = withLabel ? '9px' : '50%';

  return (
    <Box className="flex flex-row items-center gap-2 mb-2">
      <IconButton
        sx={{
          borderRadius: borderRadius
        }}
        disabled={currentIndex >= eligibleRuns.length - 1}
        onClick={() => setCurrentIndex(currentIndex + 1)}>
        <ArrowLeft />
        {withLabel && <span className="text-sm pr-2">{getRunDate(1)}</span>}
      </IconButton>
      <Box className="flex flex-row items-center gap-2 mx-4 rounded-md border px-3 py-1">
        <Text size="sm14">{getRunDate()}</Text>
        {selectedRun && (
          <Chip
            label={selectedRun.status}
            sx={{ ...chipStyles(selectedRun.status) }}
            size="small"
          />
        )}
      </Box>
      <IconButton
        sx={{
          borderRadius: borderRadius
        }}
        disabled={currentIndex <= 0}
        onClick={() => setCurrentIndex(currentIndex - 1)}>
        {withLabel && <span className="text-sm pl-2">{getRunDate(-1)}</span>}
        <ArrowRight />
      </IconButton>
    </Box>
  );
};

export default RunSelection;
